import PrivateImage from '../presentation/PrivateImage';
import React, { useRef, useState, useEffect } from 'react';
import '../../Print.css';
import { Container, Button, Divider, Grid, Paper, Toolbar, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { PDFExport } from "@progress/kendo-react-pdf";
import { getPdfExportProxyProps, savePdfWithEmbeddedImages } from "../../helpers/pdfExport";
import { Redirect } from "react-router-dom";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import GeneratedDocumentEmailAction from "./GeneratedDocumentEmailAction";
import UserService from "../../services/user.service";
import { logOut } from "../../helpers/logout";
import ActionButton from '../form/input/ActionButton';
import TextInput from '../form/input/TextInput';
import DateInput from '../form/input/DateInput';
import Popup from '../presentation/Popup';
import { NotificationManager } from "react-notifications";

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
    },
    seeMore: {
        marginTop: theme.spacing(8),
    },
    toolbarButtons: {
        '& > *': {
            margin: theme.spacing(0.5),
        },
    },
    button: {
        margin: theme.spacing(1),
    },
    marginTop: {
        marginTop: theme.spacing(2),
    },
}));

const DeliveryNoteGenerator = (props) => {
    const classes = useStyles();
    const pdfRef = useRef(null);
    const funeral_id = props.match.params;

    const [funeral, setFuneral] = useState({
        _id: "",
        service_entities: [],
        product_entities: [],
        contracting_authority: "",
        ca_address: "",
        ca_zip: "",
        ca_city: "",
        ca_phone: "",
        ca_email: "",
        deceased: ""
    });

    const [deliveryNote, setDeliveryNote] = useState({
        delivery_note_number: "",
        issue_date: new Date().toISOString().split('T')[0],
        delivery_date: new Date().toISOString().split('T')[0],
        notes: ""
    });

    const [user, setUser] = useState(null);
    const [success, setSuccess] = useState(false);
    const [showEmailPopup, setShowEmailPopup] = useState(false);
    const [emailAddress, setEmailAddress] = useState('');
    const [sendingEmail, setSendingEmail] = useState(false);

    const user_local = JSON.parse(localStorage.getItem("user")).id;
    const fixedHeightPaper = classes.paper;

    useEffect(() => {
        UserService.getUser(user_local).then(
            (response) => {
                setUser(response.data);
                // Generate delivery note number from next invoice number
                if (response.data.next_invoice_number) {
                    setDeliveryNote(prev => ({
                        ...prev,
                        delivery_note_number: `DL${response.data.next_invoice_number}`
                    }));
                }
            },
            (e) => {
                if (e.response.status === 401) {
                    logOut(props);
                    NotificationManager.warning(
                        "Potrebné sa znova prihlásiť!",
                        "Upozornenie",
                        5000
                    );
                }
            }
        );
    }, [user_local, props]);

    useEffect(() => {
        UserService.getFuneral(funeral_id.funeral_id).then(
            (response) => {
                setFuneral(response.data);
                // Set customer email if available
                if (response.data.ca_email) {
                    setEmailAddress(response.data.ca_email);
                }
                // Set delivery note number from invoice_id
                if (response.data.invoice_id) {
                    setDeliveryNote(prev => ({
                        ...prev,
                        delivery_note_number: `DL${response.data.invoice_id}`
                    }));
                }
            },
            (e) => {
                if (e.response.status === 401) {
                    logOut(props);
                    NotificationManager.warning(
                        "Potrebné sa znova prihlásiť!",
                        "Upozornenie",
                        5000
                    );
                }
            }
        );
    }, [funeral_id.funeral_id, props]);

    const handleDeliveryNoteChange = (prop) => (event) => {
        setDeliveryNote({ ...deliveryNote, [prop]: event.target.value });
    };

    const getItemPrice = (item) => {
        if (typeof item.number === 'number' && item.number > 0) {
            return item.number;
        }
        if (item.text_cost && typeof item.text_cost.cost === 'number' && item.text_cost.cost > 0) {
            return item.text_cost.cost;
        }
        return 0;
    };

    const hasPrice = (item) => {
        return getItemPrice(item) > 0;
    };

    const calculateItemTotal = (item) => {
        const price = getItemPrice(item);
        const count = item.count || 1;
        return price * count;
    };

    const calculateSubtotal = () => {
        let total = 0;
        funeral.service_entities?.forEach(item => {
            if (hasPrice(item)) {
                total += calculateItemTotal(item);
            }
        });
        funeral.product_entities?.forEach(item => {
            if (hasPrice(item)) {
                total += calculateItemTotal(item);
            }
        });
        return total;
    };

    const calculateBaseAmount = () => {
        // Prices include VAT, so extract base amount (price / 1.23)
        const totalWithVAT = calculateSubtotal();
        return totalWithVAT / 1.23;
    };

    const calculateVAT = () => {
        // Extract VAT amount from VAT-inclusive prices
        const totalWithVAT = calculateSubtotal();
        return totalWithVAT - (totalWithVAT / 1.23);
    };

    const calculateTotal = () => {
        // Total is the same as subtotal since prices already include VAT
        return calculateSubtotal();
    };

    const handleExport = () => {
        savePdfWithEmbeddedImages(pdfRef);
    };

    const handleEmailChange = (e) => {
        setEmailAddress(e.target.value);
    };

    const handleSendEmail = async () => {
        if (!emailAddress || !emailAddress.includes('@')) {
            NotificationManager.error('Zadajte platnú emailovú adresu!', 'Chyba', 5000);
            return;
        }

        setSendingEmail(true);

        try {
            const pdfBlob = await savePdfWithEmbeddedImages(pdfRef);
            
            const formData = new FormData();
            formData.append('email', emailAddress);
            formData.append('pdf', pdfBlob, `dodaci-list-${deliveryNote.delivery_note_number}.pdf`);
            formData.append('funeral_id', funeral._id);
            formData.append('document_type', 'delivery_note');

            await UserService.sendDocumentByEmail(formData);
            
            NotificationManager.success('Email bol úspešne odoslaný!', 'Úspech', 5000);
            setShowEmailPopup(false);
            setEmailAddress('');
        } catch (error) {
            if (error.response?.status === 401) {
                logOut(props);
                NotificationManager.warning('Potrebné sa znova prihlásiť!', 'Upozornenie', 5000);
            } else {
                NotificationManager.error('Nepodarilo sa odoslať email!', 'Chyba', 5000);
            }
        } finally {
            setSendingEmail(false);
        }
    };

    const showDeliveryNote = () => {
        if (!user) return <></>;

        return (
            <div className="document-preview-frame" style={{ border: '1px solid #ccc' }}>
                <PDFExport ref={pdfRef} {...getPdfExportProxyProps()} paperSize="A4" scale={0.75}
                    fileName={`dodaci_list_${deliveryNote.delivery_note_number}`}>
                    <div style={{ 
                        width: '210mm', 
                        minHeight: '297mm', 
                        padding: '15mm', 
                        fontFamily: 'Arial, sans-serif',
                        boxSizing: 'border-box'
                    }}>
                        {/* Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '25px', marginTop: '10mm' }}>
                            <div style={{ width: '50%' }}>
                                <PrivateImage width={110} src={user.logo} alt="Logo" />
                            </div>
                            <div style={{ width: '50%', textAlign: 'right' }}>
                                <h1 style={{ margin: '0', fontSize: '30px', fontWeight: 'bold' }}>DODACÍ LIST</h1>
                                <p style={{ margin: '5px 0', fontSize: '13px' }}>č. {deliveryNote.delivery_note_number}</p>
                            </div>
                        </div>

                        {/* Supplier and Customer Info */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                            <div style={{ width: '48%' }}>
                                <h3 style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 'bold' }}>Dodávateľ:</h3>
                                <div style={{ fontSize: '11px', lineHeight: '1.5' }}>
                                    <div style={{ fontWeight: 'bold' }}>{user.name}</div>
                                    <div>{user.address}</div>
                                    <div>IČO: {user.ico}</div>
                                    {user.ic_dph && <div>IČ DPH: {user.ic_dph}</div>}
                                    <div>Tel: {user.phone}</div>
                                    <div>Email: {user.email_official}</div>
                                </div>
                            </div>
                            <div style={{ width: '48%' }}>
                                <h3 style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 'bold' }}>Odberateľ:</h3>
                                <div style={{ fontSize: '11px', lineHeight: '1.5' }}>
                                    <div style={{ fontWeight: 'bold' }}>{funeral.contracting_authority}</div>
                                    <div>{funeral.ca_address}</div>
                                    <div>{funeral.ca_zip} {funeral.ca_city}</div>
                                    <div>Tel: {funeral.ca_phone}</div>
                                    <div>Email: {funeral.ca_email}</div>
                                </div>
                            </div>
                        </div>

                        {/* Deceased Info */}
                        <div style={{ marginBottom: '15px', padding: '8px 12px', backgroundColor: '#f9f9f9', border: '1px solid #ddd' }}>
                            <div style={{ fontSize: '11px' }}>
                                <strong>Zosnulý:</strong> {funeral.deceased}
                            </div>
                        </div>

                        {/* Delivery Note Details */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                            <div style={{ width: '48%', fontSize: '10px' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ padding: '3px 0' }}>Dátum vystavenia:</td>
                                            <td style={{ padding: '3px 0', fontWeight: 'bold', textAlign: 'right' }}>
                                                {new Date(deliveryNote.issue_date).toLocaleDateString('sk-SK')}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={{ padding: '3px 0' }}>Dátum dodania:</td>
                                            <td style={{ padding: '3px 0', fontWeight: 'bold', textAlign: 'right' }}>
                                                {new Date(deliveryNote.delivery_date).toLocaleDateString('sk-SK')}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Items Table */}
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '18px', fontSize: '11px' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f0f0f0' }}>
                                    <th style={{ border: '1px solid #ddd', padding: '7px', textAlign: 'left' }}>Položka</th>
                                    <th style={{ border: '1px solid #ddd', padding: '7px', textAlign: 'center', width: '75px' }}>Množstvo</th>
                                    <th style={{ border: '1px solid #ddd', padding: '7px', textAlign: 'right', width: '95px' }}>Cena/ks</th>
                                    <th style={{ border: '1px solid #ddd', padding: '7px', textAlign: 'right', width: '95px' }}>Spolu</th>
                                </tr>
                            </thead>
                            <tbody>
                                {funeral.service_entities?.map((item, index) => {
                                    if (!hasPrice(item)) return null;
                                    return (
                                        <tr key={`service-${index}`}>
                                            <td style={{ border: '1px solid #ddd', padding: '7px' }}>{item.name}</td>
                                            <td style={{ border: '1px solid #ddd', padding: '7px', textAlign: 'center' }}>{item.count || 1}</td>
                                            <td style={{ border: '1px solid #ddd', padding: '7px', textAlign: 'right' }}>
                                                {getItemPrice(item).toFixed(2)} €
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '7px', textAlign: 'right' }}>
                                                {calculateItemTotal(item).toFixed(2)} €
                                            </td>
                                        </tr>
                                    );
                                })}
                                {funeral.product_entities?.map((item, index) => {
                                    if (!hasPrice(item)) return null;
                                    return (
                                        <tr key={`product-${index}`}>
                                            <td style={{ border: '1px solid #ddd', padding: '7px' }}>{item.name}</td>
                                            <td style={{ border: '1px solid #ddd', padding: '7px', textAlign: 'center' }}>{item.count || 1}</td>
                                            <td style={{ border: '1px solid #ddd', padding: '7px', textAlign: 'right' }}>
                                                {getItemPrice(item).toFixed(2)} €
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '7px', textAlign: 'right' }}>
                                                {calculateItemTotal(item).toFixed(2)} €
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        {/* Totals */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '18px' }}>
                            <div style={{ width: '58%' }}>
                                <table style={{ width: '100%', fontSize: '12px', marginLeft: 'auto' }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ padding: '5px 8px', textAlign: 'right' }}>Základ:</td>
                                            <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 'bold', width: '110px' }}>
                                                {calculateBaseAmount().toFixed(2)} €
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={{ padding: '5px 8px', textAlign: 'right' }}>DPH 23%:</td>
                                            <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 'bold' }}>
                                                {calculateVAT().toFixed(2)} €
                                            </td>
                                        </tr>
                                        <tr style={{ backgroundColor: '#f0f0f0', fontSize: '15px' }}>
                                            <td style={{ padding: '9px 8px', textAlign: 'right', fontWeight: 'bold' }}>
                                                Celkom s DPH:
                                            </td>
                                            <td style={{ padding: '9px 8px', textAlign: 'right', fontWeight: 'bold' }}>
                                                {calculateTotal().toFixed(2)} €
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Notes */}
                        {deliveryNote.notes && (
                            <div style={{ marginBottom: '18px', fontSize: '11px' }}>
                                <h4 style={{ margin: '0 0 8px 0', fontSize: '12px' }}>Poznámka:</h4>
                                <p style={{ margin: 0 }}>{deliveryNote.notes}</p>
                            </div>
                        )}

                        {/* Footer */}
                        <div style={{ fontSize: '10px', textAlign: 'center', color: '#666', borderTop: '1px solid #ddd', paddingTop: '12px', marginTop: '30px' }}>
                            <p style={{ margin: '4px 0' }}>
                                Dodací list je vystavený elektronicky a je platný bez podpisu a pečiatky.
                            </p>
                            <p style={{ margin: '4px 0' }}>
                                {user.name} | {user.address} | IČO: {user.ico} | IČ DPH: {user.ic_dph}
                            </p>
                        </div>
                    </div>
                </PDFExport>
            </div>
        );
    };

    return (
        <Container maxWidth="lg" className={classes.seeMore}>
            <div className={classes.root}>
                <Paper className={fixedHeightPaper}>
                    <Grid container>
                        <Grid xs={12} sm={4} md={3} lg={3} item container>
                            <Toolbar>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    className={classes.button}
                                    onClick={() => setSuccess(true)}
                                >
                                    Späť na detaily
                                </Button>
                            </Toolbar>
                        </Grid>
                        <Grid xs={12} sm={8} md={9} lg={9} item container justifyContent='flex-end'>
                            <Toolbar className={`${classes.toolbarButtons} document-actions-toolbar`}>
                                <TextInput
                                    label="Číslo dodacieho listu"
                                    name="delivery_note_number"
                                    value={deliveryNote.delivery_note_number}
                                    onChange={handleDeliveryNoteChange('delivery_note_number')}
                                />
                                <DateInput
                                    label="Dátum vystavenia"
                                    name="issue_date"
                                    value={deliveryNote.issue_date}
                                    onChange={handleDeliveryNoteChange('issue_date')}
                                />
                                <DateInput
                                    label="Dátum dodania"
                                    name="delivery_date"
                                    value={deliveryNote.delivery_date}
                                    onChange={handleDeliveryNoteChange('delivery_date')}
                                />
                                <ActionButton color="primary">
                                    <Tooltip title="Pdf export">
                                        <PictureAsPdfIcon
                                            fontSize="small"
                                            onClick={handleExport}
                                        />
                                    </Tooltip>
                                </ActionButton>
                                <GeneratedDocumentEmailAction
                                    pdfRef={pdfRef}
                                    funeral={funeral}
                                    user={user}
                                />
                                {/* <ActionButton color="primary">
                                    <Tooltip title="Odoslať emailom">
                                        <EmailIcon
                                            fontSize="small"
                                            onClick={() => setShowEmailPopup(true)}
                                        />
                                    </Tooltip>
                                </ActionButton> */}
                            </Toolbar>
                        </Grid>
                    </Grid>

                    <Divider />
                    <Grid className={classes.marginTop}
                        container
                        spacing={0}
                        direction="column"
                        alignItems="center"
                        justifyContent="center"
                    >
                        {showDeliveryNote()}
                    </Grid>
                </Paper>
            </div>
            <Popup
                title={'Odoslať dodací list emailom'}
                openPopup={showEmailPopup}
                setOpenPopup={setShowEmailPopup}
                maxW='sm'
                actions={<>
                    <Button color="primary" variant="outlined" onClick={() => setShowEmailPopup(false)}>
                        Zrušiť
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={handleSendEmail}
                        disabled={sendingEmail}
                    >
                        {sendingEmail ? 'Odosiela sa...' : 'Odoslať'}
                    </Button>
                </>}
            >
                <TextInput
                    label="Emailová adresa"
                    name="email"
                    value={emailAddress}
                    onChange={handleEmailChange}
                    type="email"
                    fullWidth
                    autoFocus
                />
            </Popup>
            {success ? <Redirect to={{
                pathname: `/pohreb/${funeral_id.funeral_id}/info`,
                state: { from: props.location, number: 5 }
            }} /> : <></>}
        </Container>
    );
};

export default DeliveryNoteGenerator;
